import { memo } from 'react'

import { useTranslation } from 'react-i18next'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Spacing,
  Text,
} from '@enechain/ecloud-designsystem'

import { ChartTooltip } from '~/components/common/ChartTooltip'
import { formatCivilDateSlash } from '~/models/civilDate'
import type { VoltageClass } from '~/models/voltageBreakdown'
import type { ContractTrendState } from '~/pages/CustomerInfo/hooks/useContractTrend'
import {
  type TrendChartRow,
  VOLTAGES,
  monthTicks,
} from '~/pages/CustomerInfo/models/contractTrend'
import { VOLTAGE_I18N_KEY, formatKw } from '~/pages/CustomerInfo/models/display'

/** 電圧階級の色 (モック: 低圧 = accent / 高圧 = positive / 特別高圧 = warning)。 */
export const VOLTAGE_COLORS: Record<VoltageClass, string> = {
  low: LightModeSemanticColors.Surface.accentPrimary.value,
  high: LightModeSemanticColors.Text.positiveMid.value,
  extraHigh: LightModeSemanticColors.Text.warningHigh.value,
}

const gridColor = LightModeSemanticColors.Border.lowEmphasis.value
const tickColor = LightModeSemanticColors.Text.midEmphasis.value
const countColor = LightModeSemanticColors.Brand.purple.value

const formatPct = (part: number, total: number): string =>
  total === 0 ? '—' : `${((part / total) * 100).toFixed(1)}%`

type ChartsProps = {
  rows: TrendChartRow[]
  /** 表示する電圧階級 (`,` 区切り。 変わった時だけ作り直すための値)。 */
  voltageKey: string
}

/** 契約電力の hover: 電圧階級ごとの kW の下に合計の行を足す (ChartTooltip の系列行と同じ書式)。 */
const KwTooltip = (props: {
  active?: boolean
  label?: string | number
  payload?: ReadonlyArray<{
    name?: string | number
    value?: number | string
    color?: string
    dataKey?: string | number
    payload?: { totalKw?: number }
  }>
}): React.ReactElement => {
  const { t } = useTranslation()
  const total = props.payload?.[0]?.payload?.totalKw
  const payload =
    props.payload === undefined || total === undefined
      ? props.payload
      : [
          ...props.payload,
          {
            name: t('page:customerInfo.trend.kpi.total'),
            value: total,
            color: tickColor,
            dataKey: 'totalKw',
          },
        ]
  return (
    <ChartTooltip active={props.active} label={props.label} payload={payload} />
  )
}

/** 件数 (整数) の縦軸の目盛り: 0 から最大値まで整数の等間隔 (最大 5 刻み程度)。 */
export const integerTicks = (max: number): number[] => {
  const top = Math.max(1, Math.ceil(max))
  const step = Math.ceil(top / 4)
  const last = Math.ceil(top / step) * step
  return Array.from({ length: last / step + 1 }, (_, i) => i * step)
}

/** グラフ 2 つ。 モーダルの開閉など無関係な再描画で作り直さない (memo)。 */
const TrendCharts = memo(function TrendCharts({
  rows,
  voltageKey,
}: ChartsProps): React.ReactElement {
  const { t } = useTranslation()
  const visible = voltageKey
    .split(',')
    .filter((v) => v !== '') as VoltageClass[]
  // 日ごとの行に対して目盛りは月の初日だけ (複数年度は間引く)
  const ticks = monthTicks(rows)
  const monthLabel = (label: string): string => label.slice(0, 7)
  const countTicks = integerTicks(Math.max(0, ...rows.map((r) => r.count)))
  return (
    <Flex flexDirection="column" gap={2}>
      <Text fontWeight="bold">
        {t('page:customerInfo.trend.chart.kwTitle')}
      </Text>
      <Box
        height="260px"
        padding={1}
        border={`1px solid ${gridColor}`}
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        <ResponsiveContainer width="100%" height="100%">
          {/* 系列の構成が変わる時だけ作り直す (recharts は後から増えた系列を末尾に積むため) */}
          <AreaChart
            key={voltageKey}
            margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
            data={rows}
          >
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              interval={0}
              tick={{ fontSize: 11, fill: tickColor }}
              tickFormatter={monthLabel}
              ticks={ticks}
            />
            <YAxis
              width={64}
              tick={{ fontSize: 11, fill: tickColor }}
              tickFormatter={(v: number) => v.toLocaleString()}
            />
            <Tooltip content={<KwTooltip />} />
            <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            {visible.map((v) => (
              <Area
                key={v}
                fill={VOLTAGE_COLORS[v]}
                stroke={VOLTAGE_COLORS[v]}
                dataKey={`kw.${v}`}
                dot={false}
                fillOpacity={0.85}
                isAnimationActive={false}
                name={t(VOLTAGE_I18N_KEY[v])}
                stackId="kw"
                type="stepAfter"
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      <Text fontWeight="bold">
        {t('page:customerInfo.trend.chart.countTitle')}
      </Text>
      <Box
        height="180px"
        padding={1}
        border={`1px solid ${gridColor}`}
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
            data={rows}
          >
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              interval={0}
              tick={{ fontSize: 11, fill: tickColor }}
              tickFormatter={monthLabel}
              ticks={ticks}
            />
            <YAxis
              width={64}
              allowDecimals={false}
              domain={[0, countTicks[countTicks.length - 1]]}
              tick={{ fontSize: 11, fill: tickColor }}
              ticks={countTicks}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend iconType="line" wrapperStyle={{ fontSize: 11 }} />
            <Line
              stroke={countColor}
              dataKey="count"
              dot={false}
              isAnimationActive={false}
              name={t('page:customerInfo.trend.kpi.customers')}
              strokeWidth={2}
              type="stepAfter"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Flex>
  )
})

type Props = { state: ContractTrendState }

/**
 * 契約推移の KPI バー・契約電力の積み上げ面 (日ごとの段差)・需要家数の推移 (日ごとの段差)
 * (AC-CIN-002〜004。 モック `Customer.razor` の契約推移タブと同じ項目・並び)。
 */
export const ContractTrendControls = ({ state }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { trend } = state
  const visible = VOLTAGES.filter((v) => state.voltages.has(v))
  const kpi = trend.kpi

  return (
    <Flex flexDirection="column" gap={Spacing[3].value}>
      {kpi !== undefined && (
        <Flex
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
          columnGap={4}
          border={`1px solid ${gridColor}`}
          borderRadius="lg"
          backgroundColor="Surface.primary"
          paddingX={4}
          paddingY={2}
        >
          <Flex alignItems="baseline" gap={1}>
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:customerInfo.trend.kpi.total')}
            </Text>
            <Text textStyle="sectionTitle">{formatKw(kpi.latest.totalKw)}</Text>
            <Text fontSize="sm">kW</Text>
          </Flex>
          {visible.map((v) => (
            <Flex key={v} alignItems="baseline" gap={1}>
              <Text color="Text.midEmphasis" fontSize="sm">
                {t(VOLTAGE_I18N_KEY[v])}
              </Text>
              <Text fontWeight="bold" style={{ color: VOLTAGE_COLORS[v] }}>
                {formatKw(kpi.latest.kw[v])}
              </Text>
              <Text color="Text.midEmphasis" fontSize="sm">
                {formatPct(kpi.latest.kw[v], kpi.latest.totalKw)}
              </Text>
            </Flex>
          ))}
          <Flex alignItems="baseline" gap={1}>
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:customerInfo.trend.kpi.customers')}
            </Text>
            <Text fontWeight="bold">{kpi.latest.count.toLocaleString()}</Text>
          </Flex>
          <Flex alignItems="baseline" gap={1}>
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:customerInfo.trend.kpi.average')}
            </Text>
            <Text fontWeight="bold">{formatKw(kpi.avgTotalKw)}</Text>
            <Text color="Text.midEmphasis" fontSize="sm">
              {`${formatKw(kpi.minTotalKw)}〜${formatKw(kpi.maxTotalKw)}`}
            </Text>
          </Flex>
          <Text marginLeft="auto" color="Text.midEmphasis" fontSize="sm">
            {t('page:customerInfo.trend.kpi.asOf', {
              date: formatCivilDateSlash(kpi.latest.date),
            })}
          </Text>
        </Flex>
      )}

      {state.isError ? (
        <Text color="Text.negativeMid">
          {t('page:customerInfo.common.loadError')}
        </Text>
      ) : trend.chartRows.length === 0 ? (
        <Text color="Text.midEmphasis">
          {t('page:customerInfo.trend.empty')}
        </Text>
      ) : (
        <TrendCharts rows={trend.chartRows} voltageKey={visible.join(',')} />
      )}
    </Flex>
  )
}
